/**
 * Everything the bundle was built with, in one place.
 *
 * `VITE_` variables are compiled in, not read at run time, so a console that talks
 * to the wrong host cannot be fixed on the server — it has to be rebuilt. That is
 * exactly why these values are also rendered in the sidebar footer: the first
 * question when nothing loads is which API and which Keycloak this build is
 * pointed at, and the answer should not require unpacking a minified file.
 */

/** Build identity, injected by Vite. */
export const build = {
  version: __APP_VERSION__,
  commit: __APP_COMMIT__,
  builtAt: __APP_BUILD_TIME__,
} as const

/**
 * What the footer shows: the version and nothing else.
 *
 * Releases are numbered by hand — 1.0.0, then 1.0.1 — so the number in
 * package.json is the one an operator quotes on the telephone. The commit is kept,
 * because two builds of 1.0.0 are not the same file, but it belongs in the
 * expandable detail rather than in the label.
 */
export const versionLabel = build.version

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

/**
 * Where mdm-service's API is. Either an absolute URL (the API on another host or
 * port than the console) or a path (same origin, typically behind a reverse proxy).
 */
export const apiBaseUrl = trimTrailingSlash(
  import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
)

export const keycloak = {
  url: trimTrailingSlash(import.meta.env.VITE_KEYCLOAK_URL ?? ''),
  realm: import.meta.env.VITE_KEYCLOAK_REALM ?? '',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? '',
} as const

/** The realm's issuer — the authority oidc-client-ts resolves its metadata from. */
export const authority = `${keycloak.url}/realms/${keycloak.realm}`

export const redirectUri = `${window.location.origin}/auth/callback`

/**
 * Whether the browser will hand out `crypto.subtle`. It does so only in a secure
 * context — HTTPS, or a `localhost` origin. This matters here because the PKCE
 * code challenge is a SHA-256 of the verifier: outside a secure context there is
 * no way to compute one, and the login redirect fails before it is even built.
 */
export const secureContext = typeof window !== 'undefined' && window.isSecureContext

export const allowInsecureOidc = import.meta.env.VITE_OIDC_ALLOW_INSECURE === 'true'

/**
 * Misconfiguration a person can read, gathered before anything tries to use it.
 * An empty list means the bundle at least names a Keycloak and an API.
 */
export function configurationProblems(): string[] {
  const problems: string[] = []
  if (!keycloak.url) {
    problems.push('VITE_KEYCLOAK_URL is empty — the bundle names no Keycloak server.')
  }
  if (!keycloak.realm) {
    problems.push('VITE_KEYCLOAK_REALM is empty — the bundle names no realm.')
  }
  if (!keycloak.clientId) {
    problems.push('VITE_KEYCLOAK_CLIENT_ID is empty — the bundle names no client.')
  }
  if (!apiBaseUrl) {
    problems.push('VITE_API_BASE_URL is empty — the bundle names no API.')
  }
  if (!secureContext && !allowInsecureOidc) {
    problems.push(
      `The console is served from ${window.location.origin}, which is not a secure ` +
        'context, so the browser withholds crypto.subtle and the PKCE code ' +
        'challenge cannot be computed. Serve the console over HTTPS, or rebuild ' +
        'with VITE_OIDC_ALLOW_INSECURE=true to fall back to a plain ' +
        'authorization-code flow.',
    )
  }
  return problems
}

/** What the footer and the auth-error screen show. Never includes a secret. */
export function diagnostics(): Record<string, string> {
  return {
    version: versionLabel,
    builtAt: build.builtAt,
    origin: window.location.origin,
    api: apiBaseUrl,
    keycloak: authority,
    client: keycloak.clientId,
    redirectUri,
    secureContext: String(secureContext),
    pkce: secureContext ? 'S256' : allowInsecureOidc ? 'disabled (insecure origin)' : 'unavailable',
    commit: build.commit,
  }
}
